<template lang="html">
  <v-container class="pa-0">
    <v-dialog v-model="showCopyLoadHelpDialog"
              :full-width="!isMobile"
              max-width="500">
      <v-card class="pa-4">
        <v-toolbar card>
          <v-toolbar-title flat>Repeat a Customer Load</v-toolbar-title>
        </v-toolbar>
        <br />
        Frequently, you find that you run the same load for a customer: same customer, same pickup
        location, same delivery location, same load instructions and driver directions.  Repeating
        a load makes putting the next load in your planner simple.
        <br/>
        <br/>
        Repeating a load does not change or affect the load currently in your planner.  It creates
        a brand new load in your planner.
        <br/>
        <br/>
        <v-icon color="accent">redo</v-icon>
        <strong>Create this load again.</strong> By creating the load this way, you provide when the
        load is scheduled to be picked up next. Additionally, it will be in your repeat load list
        so it will be easy to plan this load quickly and easily.

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat
                 @click="closeCopyLoadHelpDialog">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-repeat-load-group-dialog v-if="!transaction"
                                v-model="showCopyLoadDialog"
                                :loadGroupId="planningLoad.humanId"
                                :sponsor="listingSponsorName"
                                :origin="planningLoad.loadGroup.details | originName"
                                :destination="planningLoad.loadGroup.details | destinationName"
                                :count="planningLoad.loadGroup.loadCount"
                                :isMobile="isMobile"
                                :loading="loadingBtn"
                                :company="company"
                                :load-group="planningLoad.loadGroup"
                                @cancel="closeCopyLoadDialog"
                                @newLoad="closeCopyLoadDialog"
    >

    </v-repeat-load-group-dialog>

    <v-layout column>
      <v-flex>
        <v-card>
          <v-toolbar :extended="isMobile" :dense="!isMobile" flat>
            <v-icon color="green"
                    v-if="verifiedCustomer">
              verified_user
            </v-icon>
            <v-toolbar-title color="primary">
              {{ listingSponsorName }}
              <template v-if="loadGroupNickname(planningLoad)">
                 ({{loadGroupNickname(planningLoad)}})
              </template>
            </v-toolbar-title>
            <v-spacer/>

            <template :slot="isMobile ? 'extension' : 'default'">
              <slot name="toolbarItems">
                edit toolbar items
              </slot>
            </template>
          </v-toolbar>
          <v-list>
            <v-list-tile avatar v-if="!disableCustomerEdition && editForm && !repeating">
              <v-list-tile-action>
                <v-chip color="accent" text-color="white" small>
                  <v-icon>business</v-icon>
                </v-chip>
              </v-list-tile-action>
              <v-list-tile-content class="pl-2">
                <v-combobox
                  :disabled="disableCustomerEdition"
                  :loading="loadingCompanies && !!search && !disableCustomerEdition"
                  :items="(search && search.length > 3) ? allCompanies : []"
                  :search-input.sync="search"
                  v-model="customerCompany"
                  :hide-no-data="!(search && search.length > 3)"
                  item-text="searchName"
                  class="mr-3"
                  return-object
                  hide-details
                  label="Customer company">

                  <template slot="item" slot-scope="data">
                    <v-icon
                      :color="_.get(data, 'item.accountStatus.verified', null) ? 'green' : 'transparent'">
                      verified_user
                    </v-icon>
                    <span class="ml-1">{{data.item.searchName}}</span>
                  </template>

                  <template slot="no-data">
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>
                          No results matching "<strong>{{ search }}</strong>".
                          Press <kbd>tab</kbd> to use this customer's name
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </template>
                </v-combobox>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile avatar v-if="editForm"
                         :class="{'amount-edition-tile': editForm}">
              <v-list-tile-action>
                <v-chip color="accent" text-color="white" small>
                  <v-icon>money</v-icon>
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content class="pl-2">
                  <v-float-field label="Billable Amount ($)"
                                 class="mt-0"
                                 :messages="loadedDistanceMessage"
                                 v-model="updatedBillableAmount"/>
              </v-list-tile-content>
            </v-list-tile>

            <template v-if="dispatched && !loadGroupOwnerIsSponsor(planningLoad)">
              <v-subheader>Dispatch Details</v-subheader>
              <v-list-tile>
                <v-list-tile-action>
                  <v-chip color="primary" small>
                    <v-icon color="white">share</v-icon>
                  </v-chip>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>{{ loadGroupOwnerName(planningLoad) }}</v-list-tile-title>
                  <v-list-tile-sub-title class="font-italic">
                    Dispatched by {{ planningLoad.createdByDisplayName }}
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>

            <v-list-tile avatar v-if="repeating || planningLoad.nickname">
              <v-list-tile-action>
                <v-chip text-color="white" small color="accent">
                  <v-icon>vpn_key</v-icon>
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content class="pl-2">
                <v-list-tile-title v-if="planningLoad.nickname">{{ planningLoad.nickname }}</v-list-tile-title>
                <v-list-tile-sub-title v-else>give this repeat load a nickname</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile>
              <v-list-tile-action>
                <v-chip color="green" text-color="white" small>
                  ${{ loadedDistanceRate }}
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content>
                <v-list-tile-title>
                  Load Pays ${{ updatedBillableAmount || loadAmount || 0}} each
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  ${{ loadedDistanceRate }}/{{ siteDistanceMeasureUnitLabel }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile avatar v-if="!transaction">
              <v-list-tile-action>
                <v-chip color="accent" text-color="white" small>
                  {{ displayLoadCount }}
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content>
                <span class="mr-2 pl-2 pr-2"
                      v-if="editForm">
                  <v-truck-counter-field v-model="updatedLoadTruckCount"
                                         :min="planningLoadGroupActiveBookedTruckCount(planningLoad)">
                  <v-icon slot="increment">add</v-icon>
                  <v-icon slot="decrement">remove</v-icon>
                </v-truck-counter-field>
                </span>
                <v-list-tile-title class="pl-2" v-else>
                  <span v-if="planningLoad.loadGroup.loadCount === 1">load needs a truck</span>
                  <span v-else-if="dispatched">
                    <span v-if="displayLoadCount === 1">load needs a truck</span>
                    <span v-else>loads need a truck</span>
                  </span>
                  <span v-else>
                    of <strong>{{ planningLoad.loadGroup.loadCount }}</strong> loads need a truck
                  </span>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-subheader>Load Details</v-subheader>
            <v-list-tile avatar v-if="!repeating">
              <v-list-tile-action>
                <v-chip text-color="white" small color="accent">
                  <v-icon>event</v-icon>
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content class="pl-2">
                <template v-if="editForm">
                  <date-picker
                    input-id="pickupTimePickerPreview"
                    placeholder="Pickup Date"
                    type="datetime"
                    v-model="updatedPickupTime"
                    format="cccc, LLLL dd yyyy hh:mm a"
                    input-class="picker"
                    :phrases="{ok: 'Continue', cancel: 'Cancel'}"
                    :hour-step="1"
                    :minute-step="60"
                    :week-start="7"
                    use12-hour
                    auto>
                  </date-picker>
                  <v-list-tile-sub-title>
                    Booked aren't modified when date changes.
                  </v-list-tile-sub-title>
                </template>
                <template v-else>
                  <span @click="enterEditMode">
                    {{ planningLoad.loadGroup.scheduledPickupTime | formatDate }}
                  </span>
                </template>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile avatar>
              <v-list-tile-action>
                <v-chip color="accent" text-color="white" small>
                  <v-icon>arrow_upward</v-icon>
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content v-if="disableAddressView">
                <v-list-tile-title>
                  {{ planningLoad.loadGroup.details | originRegion }}
                </v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content v-else class="pl-2">
                <AddressSearch v-if="editForm && !repeating"
                               label="From (Origin Address)"
                               v-model="updatedOriginLocation">
                </AddressSearch>
                <template v-else>
                  <v-list-tile-title @click="enterEditMode">{{ planningLoad.loadGroup.details | originName }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ planningLoad.loadGroup.details | originAddress }}</v-list-tile-sub-title>
                </template>
              </v-list-tile-content>

            </v-list-tile>

            <v-list-tile avatar>
              <v-list-tile-action>
                <v-chip color="accent" small text-color="white">
                  <v-icon>play_for_work</v-icon>
                </v-chip>
              </v-list-tile-action>

              <v-list-tile-content v-if="disableAddressView">
                <v-list-tile-title>
                  {{ planningLoad.loadGroup.details | destinationRegion }}
                </v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content v-else class="pl-2">
                <AddressSearch v-if="editForm && !repeating"
                               label="To (Destination Address)"
                               v-model="updatedDestinationLocation">
                </AddressSearch>
                <template v-else>
                  <v-list-tile-title @click="enterEditMode">{{ planningLoad.loadGroup.details | destinationName }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ planningLoad.loadGroup.details | destinationAddress }}</v-list-tile-sub-title>
                </template>
              </v-list-tile-content>
            </v-list-tile>

            <v-subheader>Requirements and Instructions</v-subheader>

              <v-list-tile v-if="requireWashout">
                <v-list-tile-action>
                  <icon-base width="35" height="35" icon-color="#352208"
                             icon-name="hose"><icon-hose />
                  </icon-base>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Washout Before Loading</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-list-tile v-if="requireWoodChips">
                <v-list-tile-action>
                  <icon-base width="35" height="35" icon-color="#352208"
                             viewBox="0 0 5 5" icon-name="wood">
                    <icon-wood />
                  </icon-base>
                </v-list-tile-action>

                <v-list-tile-content>
                  <v-list-tile-title>Provide Fresh Wood Chips</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-list-tile v-if="requireWeatherboards">
                <v-list-tile-action>
                  <icon-base width="35" height="35"
                             icon-name="cold" icon-color="#352208">
                    <icon-cold />
                  </icon-base>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Must Use Weatherboards</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

            <v-container v-if="editForm">
              <v-layout>
                <v-flex md6 lg6>
                  <v-textarea v-model="updatedDispatchInstructions"
                              :rows="2"
                              label="Dispatcher instructions">
                  </v-textarea>
                </v-flex>
              </v-layout>
            </v-container>
            <v-list-tile v-else-if="dispatchInstructions">
              <v-list-tile-content>
                <v-list-tile-title>
                  <b>Dispatcher instructions: </b>
                  {{dispatchInstructions}}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-else>
              <v-list-tile-content>
                <v-list-tile-title>
                  <em>Dispatcher instructions not provided</em>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-container v-if="editForm">
              <v-layout>
                <v-flex md6 lg6>
                  <v-textarea v-model="updatedDriverInstructions"
                              :rows="2"
                              label="Driver instructions">
                  </v-textarea>
                </v-flex>
              </v-layout>
            </v-container>
            <v-list-tile v-else-if="driverInstructions">
              <v-list-tile-content>
                <v-list-tile-title>
                  <b>Driver instructions: </b>
                  {{driverInstructions}}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-else>
              <v-list-tile-content>
                <v-list-tile-title><em>Driver instructions not provided</em></v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile v-if="!requireWashout && !requireWoodChips && !requireWeatherboards">
              <v-list-tile-content>
                <v-list-tile-title><em>No provided hauling conditions</em></v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
import API from '@/api';

import { mapGetters } from 'vuex';
import * as Timestamps from '@/types/timestamp';

import companyNamesMixin from '@/mixins/companyNames';
import VTruckCounterField from '@/components/VTruckCounterField';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import AddressSearch from '@/components/AddressSearch';
import VFloatField from '@/components/VFloatField';
import IconBase from '@/components/IconBase';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconCold from '@/components/icons/IconCold';
import DatePicker from '@/components/DatePicker';
import VRepeatLoadGroupDialog from '@/components/RepeatLoadGroupDialog';
import unitsOfMeasurePlugin from '../../mixins/unitsOfMeasurePlugin';

export default {
  name: 'VEditLoadDetails',
  mixins: [
    companyNamesMixin,
    loadGroupMixin,
    planningLoadGroupsMixin,
    unitsOfMeasurePlugin,
  ],
  props: {
    disableAddressView: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      required: true,
    },
    planningLoad: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: null,
    },
    dispatched: {
      type: Boolean,
      default: false,
    },
    repeating: {
      type: Boolean,
      default: false,
    },
    transaction: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    updatedLoadTruckCount: null,
    updatedPickupTime: null,
    updatedOriginLocation: null,
    updatedDestinationLocation: null,
    updatedCustomer: null,
    updatedBillableAmount: null,
    updatedDispatchInstructions: null,
    updatedDriverInstructions: null,
    editForm: false,
    savingLoad: false,
    billableAmount: null,
    customerCompany: null,

    loadingBtn: false,
    showCopyLoadDialog: false,
    showCopyLoadHelpDialog: false,
    search: null,
    loadingCompanies: false,
    copyLoadButtons: [0, 1, 2],
  }),
  components: {
    VTruckCounterField,
    VRepeatLoadGroupDialog,
    AddressSearch,
    VFloatField,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
    DatePicker,
  },
  watch: {
    planningLoad(newVal, oldVal) {
      if (newVal.humanId !== oldVal.humanId) {
        this.formInit();
      }
    },
    // read-only is a prop
    readOnly(val) {
      this.editForm = !val && !this.dispatched;
    },
    editForm(val) {
      this.updatedBillableAmount = val ? this.loadAmount : null;
      this.updatedDispatchInstructions = val ? this.dispatchInstructions : null;
      this.updatedDriverInstructions = val ? this.driverInstructions : null;
      this.updatedLoadTruckCount = val ? _.get(this.planningLoad, 'loadGroup.loadCount') : null;
      this.updatedPickupTime = val ? this.planningLoad.loadGroup.scheduledPickupTime : null;
      this.updatedOriginLocation = val ? {
        description: _.get(this.planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeName', null),
        place_id: _.get(this.planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeId', null),
      } : null;

      this.updatedDestinationLocation = val ? {
        description: _.get(this.planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeName', null),
        place_id: _.get(this.planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeId', null),
      } : null;

      this.customerCompany = val ? _.get(this.planningLoad, 'loadGroup.details.customerCompany.name', null) : null;
    },
    isEditLoadDirty(val) {
      if (!this.readOnly) {
        this.$emit('onFormChange', !!val);
      }
    },
    updatedOriginLocation: {
      handler() {
        this.$emit('updateOrigin', this.dirtyOrigin ?
          _.get(this.updatedOriginLocation, 'place_id', null) : null);
      },
      deep: true,
    },
    updatedDestinationLocation: {
      handler() {
        this.$emit('updateDestination', this.dirtyDestination ?
          _.get(this.updatedDestinationLocation, 'place_id', null) : null);
      },
      deep: true,
    },
  },
  computed: {
    _() {
      return _;
    },
    ...mapGetters('companies', {
      allCompanies: 'catalog',
    }),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    listingSponsorName() {
      return this.updatedCustomerName ||
        this.listingCustomerName(this.planningLoad) ||
        this.listingProducerName(this.planningLoad);
    },
    updatedCustomerName() {
      return _.get(this.planningLoad, 'loadGroup.details.customerCompany.name', null) || this.updatedCustomer;
    },
    verifiedCustomer() {
      return this.isLoadGroupCustomerVerified(this.planningLoad);
    },
    disableCustomerEdition() {
      return !!_.get(this.planningLoad, 'loadGroup.details.customerCompany.id', false);
    },
    loadAmount() {
      if (this.dispatched) {
        return this.planningLoad.payableAmount;
      }
      return this.planningLoad.loadGroup.amount;
    },
    loadedDistanceRate() {
      if (this.dispatched) {
        return this.loadedDistanceDispatchRate;
      }
      return this.loadedDistanceBilledRate;
    },
    loadedDistanceMessage() {

      const loadedDistanceBilledRate = this.distanceRateLabel(this.updatedBillableAmount, this.loadedMiles);
      return [`${this.distanceLabel(this.loadedMiles)}, ${loadedDistanceBilledRate} per loaded ${this.siteDistanceMeasureUnitLabel}. Based on Google Maps`];
    },
    loadedDistanceBilledRate() {
      return this.distanceRate(this.planningLoad.loadGroup.amount, this.loadedMiles);
    },
    loadedDistanceDispatchRate() {
      const amount = this.planningLoad.payableAmount;

      return this.distanceRate(amount, this.loadedMiles);
    },
    loadedMiles() {
      return this.loadGroupLoadedMiles(this.planningLoad);
    },
    isEditLoadDirty() {
      if (this.planningLoad == null) {
        return false;
      }

      return this.dirtyCount || this.dirtyPickUp || this.dirtyOrigin ||
        this.dirtyDestination || this.dirtyCustomer || this.dirtyAmount ||
        this.dirtyDriverInstructions || this.dirtyDispatchInstructions;
    },
    dirtyCount() {
      return this.updatedLoadTruckCount !== null &&
        this.updatedLoadTruckCount !== this.planningLoad.loadGroup.loadCount;
    },
    dirtyPickUp() {
      return this.updatedPickupTime &&
        Timestamps.localizedTimestamp(this.updatedPickupTime) !== this.planningLoad.loadGroup.scheduledPickupTime;
    },
    dirtyCustomer() {
      const customerName = _.get(this.planningLoad, 'loadGroup.details.customerCompany.name', null);
      const updatedName = _.get(this.updatedCustomer, 'companyName', null) || this.updatedCustomer;

      return this.updatedCustomer && updatedName !== customerName;
    },
    dirtyAmount() {
      return this.updatedBillableAmount &&
        this.updatedBillableAmount !== this.planningLoad.loadGroup.amount;
    },
    dirtyOrigin() {
      return this.updatedOriginLocation &&
        _.get(this.planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeId', null) !==
        _.get(this.updatedOriginLocation, 'place_id', null);
    },
    dirtyDestination() {
      return this.updatedDestinationLocation &&
        _.get(this.planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeId', null) !==
        _.get(this.updatedDestinationLocation, 'place_id', null);
    },
    dirtyDriverInstructions() {
      return this.updatedDriverInstructions &&
        this.updatedDriverInstructions !== this.driverInstructions;
    },
    dirtyDispatchInstructions() {
      return this.updatedDispatchInstructions &&
        this.updatedDispatchInstructions !== this.dispatchInstructions;
    },
    requireWashout() {
      return this.loadGroupRequiresWashout(this.planningLoad);
    },
    requireWeatherboards() {
      return this.loadGroupRequiresWeatherboards(this.planningLoad);
    },
    requireWoodChips() {
      return this.loadGroupRequiresWoodChips(this.planningLoad);
    },
    dispatchInstructions() {
      return this.loadGroupDispatchInstructions(this.planningLoad);
    },
    driverInstructions() {
      return this.loadGroupDriverInstructions(this.planningLoad);
    },
    displayLoadCount() {
      const lgAvailableCount = this.planningLoad.loadGroup.availableLoadCount;
      if (this.dispatched) {
        return _.min([this.planningLoad.visibleCount, lgAvailableCount]);
      }

      return lgAvailableCount;
    },
  },
  methods: {
    async loadCompanies() {
      this.loadingCompanies = true;
      await this.$store.dispatch('companies/getAllCompanies');
      this.loadingCompanies = false;
    },
    async getEditedLoad() {
      this.savingLoad = true;
      this.$emit('savingLoad', true);
      // let's start with the immutable (for now) stuff
      const load = {
        humanId: this.planningLoad.humanId,
        requireWashout: this.requireWashout,
        requireWeatherboards: this.requireWeatherboards,
        requireWoodChips: this.requireWoodChips,
      };

      if (typeof this.customerCompany === 'object' ||
        this.customerCompany !== _.get(this.planningLoad, 'customerCompany.name', null)) {
        this.updatedCustomer = this.customerCompany;
      }

      if (this.dirtyCustomer) {
        if (this.updatedCustomer.id) {
          load.customerCompanyId = this.updatedCustomer.id;
          load.customerCompanyName = this.updatedCustomer.companyName;
        } else {
          load.customerCompanyName = this.updatedCustomer;
        }
      } else {
        load.customerCompanyId = _.get(this.planningLoad, 'loadGroup.details.customerCompany.id', null);
        load.customerCompanyName = _.get(this.planningLoad, 'loadGroup.details.customerCompany.name', null);
      }

      delete load.customerCompany;

      if (this.dirtyCount) {
        load.loadCount = this.updatedLoadTruckCount;
      } else {
        load.loadCount = this.planningLoad.loadGroup.loadCount;
      }

      if (this.dirtyPickUp) {
        load.pickupTime = Timestamps.localizedTimestamp(this.updatedPickupTime);
      } else {
        load.pickupTime = this.planningLoad.loadGroup.scheduledPickupTime;
      }

      if (this.dirtyOrigin) {
        load.pickupLocation = {
          googlePlaceId: this.updatedOriginLocation.place_id,
          description: this.updatedOriginLocation.description,
        };
      } else {
        load.pickupLocation = {
          googlePlaceId: _.get(this.planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeId', null),
          description: _.get(this.planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeName', null),
        };
      }

      if (this.dirtyDestination) {
        load.deliveryLocation = {
          googlePlaceId: this.updatedDestinationLocation.place_id,
          description: this.updatedDestinationLocation.description,
        };
      } else {
        load.deliveryLocation = {
          googlePlaceId: _.get(this.planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeId', null),
          description: _.get(this.planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeName', null),
        };
      }

      if (this.dirtyDestination || this.dirtyOrigin) {
        load.loadedMiles = await API.map.getDistance(load.pickupLocation.googlePlaceId,
          load.deliveryLocation.googlePlaceId);
      } else {
        load.loadedMiles = this.planningLoad.loadGroup.details.loadedMiles;
      }

      if (this.dirtyDriverInstructions) {
        load.driverInstructions = this.updatedDriverInstructions;
      } else {
        load.driverInstructions = this.driverInstructions;
      }

      if (this.dirtyDispatchInstructions) {
        load.dispatchInstructions = this.updatedDispatchInstructions;
      } else {
        load.dispatchInstructions = this.dispatchInstructions;
      }

      return load;
    },
    async saveRepeatLoad() {
      if (!this.editForm) {
        console.error('Should not be saving load details when in read-only mode');
        return;
      }

      try {
        const load = await this.getEditedLoad();

        if (this.dirtyAmount) {
          load.billableAmount = this.updatedBillableAmount;
        } else {
          load.billableAmount = _.get(this.planningLoad, 'payableAmount', null);
        }

        if (this.planningLoad.nickname) {
          load.nickname = this.planningLoad.nickname;
        }

        const updatedLoad = await API.company.saveRepeatLoad({
          loadId: load.humanId,
          body: load,
        });
        this.$emit('refresh', updatedLoad.data);
        this.formInit();
      } catch (error) {
        console.error(error);
        this.$store.dispatch('applications/setErrorNotification', {
          message: 'An error occurred, please try again later',
        });
      } finally {
        this.$emit('savingLoad', false);
        this.savingLoad = false;
      }
    },
    async saveLoad() {
      if (!this.editForm) {
        console.error('Should not be saving load details when in read-only mode');
        return;
      }

      try {
        const load = await this.getEditedLoad();
        if (this.dirtyAmount) {
          load.customerBillableAmount = this.updatedBillableAmount;
        } else {
          load.customerBillableAmount = _.get(this.planningLoad, 'loadGroup.amount', null);
        }

        const updatedLoad = await API.planner.updateLoad(load);
        this.$emit('refresh', updatedLoad.data);
        this.formInit();
      } catch (error) {
        console.error(error);
        this.$store.dispatch('applications/setErrorNotification', {
          message: 'An error occurred, please try again later',
        });
      } finally {
        this.$emit('savingLoad', false);
        this.savingLoad = false;
      }
    },
    listingCustomerName(listing) {
      return _.get(listing, 'loadGroup.details.customerCompany.name');
    },
    listingProducerName(listing) {
      return _.get(listing, 'loadGroup.details.listingCompany.name');
    },
    formInit() {
      this.updatedLoadTruckCount = null;
      this.updatedPickupTime = null;
      this.updatedOriginLocation = null;
      this.updatedBillableAmount = null;
      this.updatedCustomer = null;
      this.customerCompany = null;

      this.updatedDestinationLocation = null;

      this.editForm = false;
    },
    openCopyLoadHelpDialog() {
      this.copyLoadButtons = [0, 1, 2];
      this.showCopyLoadHelpDialog = true;
    },
    closeCopyLoadHelpDialog() {
      this.copyLoadButtons = [0, 1, 2];
      this.showCopyLoadHelpDialog = false;
    },
    openCopyLoadDialog() {
      this.copyLoadButtons = [0, 1, 2];
      this.showCopyLoadDialog = true;
    },
    closeCopyLoadDialog() {
      this.showCopyLoadDialog = false;
      this.copyLoadButtons = [0, 1, 2];
    },
    enterEditMode() {
      if (!this.readOnly && !this.dispatched) {
        this.editForm = true;
      }
    },
  },
  created() {
    if (this.dispatched) {
      if (this.readOnly === false) {
        console.error('Overriding read only attribute on EditLoadDetails for load ' + this.planningLoad.humanId);
      }
    }
    this.formInit();
    this.loadCompanies();

    this.$bus.$on('saveEditLoad', this.saveLoad);
    this.$bus.$on('saveEditRepeatLoad', this.saveRepeatLoad);
  },
  destroyed() {
    this.$bus.$off('saveEditLoad', this.saveLoad);
    this.$bus.$off('saveEditRepeatLoad', this.saveRepeatLoad);
  },
};
</script>

<style>

  .amount-edition-tile .v-list__tile--avatar{
    min-height: 76px;
  }

</style>



// WEBPACK FOOTER //
// src/components/InfoWidgets/EditLoadDetails.vue